/**
 * The two sounds that bracket a voice call: one when it opens and a pop when it
 * ends. The opening sound stands in for a spoken greeting — the assistant no
 * longer speaks first, so this is what tells the customer the line is theirs.
 *
 * They live outside React because the overlay is not where a call begins or
 * ends: the End button, the close icon and navigating off the origin chat all
 * funnel through the same `voiceModeClosed` dispatch, and that dispatch also
 * unmounts the surface. The middleware owns the session lifecycle, so it owns
 * these too.
 *
 * Deliberately their own elements rather than the shared voice sink: that one's
 * `srcObject` is torn down by the RTC at exactly the moment the pop plays.
 */

const START_CALL_SOUND_URL = "/sounds/voice-call-start.m4a"
const END_CALL_SOUND_URL = "/sounds/voice-call-end.m4a"
// Just past the clips themselves, because something waits on each: the
// microphone stays closed until the start sound settles, and the call's capture
// stays open until the end sound does. A generous ceiling would cost the
// customer their opening sentence on any browser where the element reports
// nothing back — a decode that fails, or a `play()` too old to return a promise.
const CALL_SOUND_TIMEOUT_MS = 700

function createCallSound(url: string) {
  let element: HTMLAudioElement | null = null

  return () => {
    if (typeof window === "undefined") return null
    if (element) return element

    // An HTMLAudioElement, not Web Audio: iOS routes element playback through
    // the "Playback" audio session, which the hardware silent switch doesn't
    // mute. See audio-session-unlock.ts.
    const audio = new Audio(url)
    audio.preload = "auto"
    element = audio
    return audio
  }
}

const startCallAudio = createCallSound(START_CALL_SOUND_URL)
const endCallAudio = createCallSound(END_CALL_SOUND_URL)

// Called when voice mode opens, so each sound lands on its moment rather than a
// network round-trip after it. Constructing the elements is the whole job —
// `preload="auto"` buffers them. An explicit load() would additionally abort a
// pop still playing from a call that ended moments ago.
export function primeCallSounds(): void {
  startCallAudio()
  endCallAudio()
}

// Resolves when the clip has finished, or immediately when it cannot play.
function playCallSound(audio: HTMLAudioElement | null): Promise<void> {
  if (!audio) return Promise.resolve()

  // Restart rather than resume, so a rapid open/close/open sounds every time.
  if (audio.currentTime !== 0) {
    audio.currentTime = 0
  }
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, CALL_SOUND_TIMEOUT_MS)
    const settle = () => {
      clearTimeout(timer)
      resolve()
    }
    audio.onended = settle
    audio.onerror = settle
    // An autoplay policy can decline this. Wrapped, because `play()` predates
    // its own promise and still returns undefined on older WebKit.
    void Promise.resolve(audio.play()).catch(settle)
  })
}

/**
 * The call is live and the microphone is yours — played at the moment the
 * session can actually hear a reply, never at the click.
 *
 * The microphone is held closed until this settles: the sound comes out of the
 * speakers, and echo cancellation has not converged on the first thing the page
 * ever plays.
 */
export function playStartCallSound(): Promise<void> {
  return playCallSound(startCallAudio())
}

/**
 * The call has ended.
 *
 * Request this while the call's microphone capture is still live, and keep the
 * tracks live until it settles. iOS permits playback with no gesture behind it
 * only while the page is capturing, so a pop requested after the tracks stopped
 * was silent on device (and fine in every desktop browser). A close by
 * navigation carries no gesture at all, so the live capture is also what lets
 * the pop sound there.
 */
export function playEndCallSound(): Promise<void> {
  return playCallSound(endCallAudio())
}
